import { useRouter } from 'vue-router';
import { useApolloClient } from '@vue/apollo-composable';
import { logoutWithCookies } from '@ugi/core';
import { UnregisterMyPushSubscriptionDocument } from '@ugi/graphql';
import { usePushSubscription } from '@/shared/composables/usePushSubscription';
import { useAuthStore } from '@/stores/auth';
import { getLogoutUrl } from '../tools/auth';

export const useLogout = () => {
  const router = useRouter();
  const authStore = useAuthStore();
  const { client } = useApolloClient();
  const { getSubscribedEndpoint, unsubscribe } = usePushSubscription();

  /**
   * Signing out takes this browser's push with it. Otherwise the device row
   * stays with the account that left, and its task and chat alerts keep
   * appearing here for whoever sits down next. Best-effort: a failure must
   * never keep someone signed in.
   */
  const dropDevicePush = async (): Promise<void> => {
    let endpoint: string | null = null;

    try {
      endpoint = await getSubscribedEndpoint();

      if (endpoint) {
        await client.mutate({
          mutation: UnregisterMyPushSubscriptionDocument,
          variables: { input: { endpoint } },
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      // Whatever the server said, this browser stops listening: with the local
      // subscription gone the push service answers 410 on the next delivery
      // and the server row is pruned then.
      if (endpoint) {
        await unsubscribe();
      }
    }
  };

  const logout = async (): Promise<void> => {
    // Mute the cross-tab SESSION_LOGGED_OUT handler on this tab — the server
    // publishes the event during logoutWithCookies and we'd otherwise race
    // with our own teardown (and crash MainLayout via its $reset).
    authStore.isLoggingOut = true;

    try {
      await dropDevicePush();
      await logoutWithCookies(getLogoutUrl());

      // clearStore aborts in-flight queries with an "Invariant Violation",
      // which is benign in this context — we're tearing it all down anyway.
      try {
        await client.clearStore();
      } catch {
        // ignore
      }

      // Refetch instead of nulling user.value: MainLayout is still mounted
      // until the route transition to /login completes, and its template
      // dereferences currentUser. With the session cookie gone, the API
      // returns Guest, which the auth middleware accepts on /login.
      await authStore.fetchMe();

      await router.replace({ name: 'Login' });
    } finally {
      authStore.isLoggingOut = false;
    }
  };

  return {
    logout,
  };
};
